// domain/model/Npi.js

/**
 * Npi — the National Provider Identifier a party holds in the NPPES registry.
 *
 * A VALUE OBJECT: no identity of its own, because the number IS the identity. Two providers cannot
 * hold the same one, but comparing two NPIs is comparing two values, and nothing anywhere needs to
 * tell one instance of `1234567893` from another.
 *
 * A TYPE RATHER THAN A STRING, and the reason is the failure it prevents. An externally assigned
 * identifier held as a bare field on a party is the shape that loses which issuing system it came
 * from — and an NPI is not self-describing, since at least one state program resolves claims by
 * matching it together with a taxonomy and a ZIP against an internal id of its own. The number is a
 * name this party holds inside NPPES, and nowhere else.
 *
 * IT DOES NOT CARRY WHETHER IT IS TYPE 1 OR TYPE 2. The registry distinguishes an individual's NPI
 * from an organization's, and the number does not encode which — so a `type` field here would have
 * to be set by hand and could disagree with the record holding it. The HOLDER says which it is: a
 * Provider holds an individual's, and an organization's belongs to the billing identity that bills
 * under it. An individual can neither have subparts nor be one, so the two can never be confused by
 * their holder.
 *
 * A PAYER'S OWN IDENTIFIER IS NOT ONE OF THESE. A PTAN or a state Medicaid provider id exists only
 * inside that payer's system and is issued per enrollment, so one person billing under two tax ids
 * holds two of them. Those live on the enrollment, which is what keeps them from collapsing into a
 * single field on a person.
 *
 * @domainObject value-object
 * @invariant Exactly ten digits. Anything else is not an NPI and is refused rather than stored.
 * @invariant Frozen, and compared by value.
 * @invariant Carries no issuer field: NPPES is the only issuer of this type, so naming it on every
 *   instance would restate what the type already says.
 * @typedef {Object} Npi
 * @property {string} value - the ten digits, exactly as the registry holds them
 */

const TEN_DIGITS = /^\d{10}$/;

/**
 * The constant NPPES prefixes onto an NPI before running the check digit, so that the result cannot
 * collide with an identifier from another issuer using the same algorithm.
 */
const CHECK_DIGIT_PREFIX = '80840';

/**
 * Whether a string is shaped like an NPI at all.
 *
 * SEPARATE FROM THE CHECK DIGIT, and deliberately. Shape is what this type refuses outright; the
 * check digit is a stronger claim about the same string, and whether a failing one blocks a save or
 * warns is a product decision rather than a property of the value. Collapsing them would settle that
 * decision here, where nobody would look for it.
 *
 * @param {*} value
 * @returns {boolean}
 */
export function isNpiShaped(value) {
  return typeof value === 'string' && TEN_DIGITS.test(value);
}

/**
 * Whether the tenth digit is the one the first nine imply.
 *
 * The registry computes it with the Luhn algorithm over `80840` plus the first nine digits, which is
 * why a transposed pair or a single mistyped digit almost always fails here and a plausible-looking
 * number does not reach a payer. It is a typo check and nothing more: a well-formed number that was
 * never issued passes this and would still be rejected by a payer.
 *
 * @param {string} value - a ten-digit string; anything else is false
 * @returns {boolean}
 */
export function hasValidCheckDigit(value) {
  if (!isNpiShaped(value)) return false;

  const digits = `${CHECK_DIGIT_PREFIX}${value.slice(0, 9)}`;
  let sum = 0;

  // Right to left, doubling every second digit. The check digit is appended after this string, so
  // its rightmost digit is the second from the right of the whole number and is doubled first.
  for (let i = digits.length - 1; i >= 0; i -= 1) {
    const digit = Number(digits[i]);
    const doubled = (digits.length - 1 - i) % 2 === 0;

    if (!doubled) {
      sum += digit;
    } else {
      const product = digit * 2;
      sum += product > 9 ? product - 9 : product;
    }
  }

  return (10 - (sum % 10)) % 10 === Number(value[9]);
}

/**
 * Builds an NPI, or returns null when the value is not one.
 *
 * NULL RATHER THAN A THROW, because absence is the ordinary case: most people have no NPI recorded,
 * and a reader asking for one has to handle that either way. A malformed stored value reads as
 * absent for the same reason it reads as absent everywhere else here — it names no provider, and an
 * item that cannot name one is held rather than guessed at.
 *
 * The check digit is NOT enforced. See `hasValidCheckDigit` for why that policy lives with the
 * caller.
 *
 * @param {*} value
 * @returns {Npi|null}
 */
export function createNpi(value) {
  if (!isNpiShaped(value)) return null;

  return Object.freeze({ value });
}

/**
 * @param {Npi|null|undefined} npi
 * @returns {string|null}
 */
export function npiValue(npi) {
  return npi?.value ?? null;
}

/**
 * Whether two NPIs say the same thing. Value objects compare by value.
 *
 * @param {Npi|null|undefined} a
 * @param {Npi|null|undefined} b
 * @returns {boolean}
 */
export function isSameNpi(a, b) {
  return npiValue(a) === npiValue(b);
}
